import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonModal from '../Common-components/Modal/modal';
import '../../css/Search/searchCard.css';
import { ReactComponent as RadioNormal } from '../../assets/icons/svg/radioBtnOutLine.svg';
import { ReactComponent as RadioChk } from '../../assets/icons/svg/radioBtnFilled.svg';
import { ReactComponent as Dot } from '../../assets/icons/svg/dot.svg';

const NO_IMAGE_FALLBACK =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="480" height="320"><rect width="100%" height="100%" fill="%23f5f5f5"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-family="Arial, sans-serif" font-size="24">No Image</text></svg>';

function SearchCard({ index, cardId, name, image, imageCandidates, period, route, value }) {
  const navigate = useNavigate();

  // 터치하는 환경 감지
  const isTouchDevice = useMemo(() => { 
    if (typeof window === 'undefined') return false;
    const mq = window.matchMedia ? window.matchMedia('(pointer: coarse)') : { matches: false };
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || mq.matches;
  }, []);

  const [showFloating, setShowFloating] = useState(false);
  const [floatingPos, setFloatingPos] = useState({ x: 0, y: 0 });
  const [floatingMsg, setFloatingMsg] = useState('');

  const normalizedImageList = useMemo(() => {
    const list = [];
    if (Array.isArray(imageCandidates)) {
      imageCandidates.forEach((url) => {
        if (typeof url === 'string') {
          const trimmed = url.trim();
          if (trimmed && !list.includes(trimmed)) {
            list.push(trimmed);
          }
        }
      });
    }
    if (typeof image === 'string') {
      const trimmed = image.trim();
      if (trimmed && !list.includes(trimmed)) {
        list.unshift(trimmed);
      }
    }
    return list;
  }, [image, imageCandidates]);

  const [imageIndex, setImageIndex] = useState(0);
  const [displayImage, setDisplayImage] = useState(() => normalizedImageList[0] ?? '');
  const fallbackAppliedRef = useRef(false);

  useEffect(() => {
    setImageIndex(0);
    fallbackAppliedRef.current = false;
    setDisplayImage(normalizedImageList[0] ?? '');
  }, [normalizedImageList]);

  useEffect(() => {
    if (displayImage !== NO_IMAGE_FALLBACK) {
      fallbackAppliedRef.current = false;
    }
  }, [displayImage]);

  const handleImageError = () => {
    setImageIndex((prev) => {
      const next = prev + 1;
      if (next < normalizedImageList.length) {
        setDisplayImage(normalizedImageList[next]);
        return next;
      }
      if (!fallbackAppliedRef.current) {
        fallbackAppliedRef.current = true;
        setDisplayImage(NO_IMAGE_FALLBACK);
      }
      return prev;
    });
  };

  const tonnage = useMemo(() => {
    if (!Array.isArray(value)) return '';
    const itemWithTonnage = value.find((item) => item && (item.tonnage || item.tonage));
    const raw = itemWithTonnage?.tonnage ?? itemWithTonnage?.tonage;
    const numeric = typeof raw === 'string' ? Number(raw.replace(/,/g, '')) : Number(raw);
    if (Number.isFinite(numeric)) return numeric.toLocaleString();
    return raw ?? '';
  }, [value]);

  // 1) 날짜 정렬(출발일 오름차순) — 업데이트 순, Top7 제한 제거
  const sortedByDepDate = useMemo(() => {
    if (!value) return [];
    return [...value].sort(
      (a, b) => new Date(a.depDate).getTime() - new Date(b.depDate).getTime()
    );
  }, [value]);
  // 1) 날짜 정렬(업데이트 desc → 상위7개 → 출발일 asc)
  // const sortedAndLimitedValues = useMemo(() => {
  //   if (!value) return [];
  //   const byUpdatedDesc = [...value].sort(
  //     (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
  //   );
  //   const top7 = byUpdatedDesc.slice(0, 7);
  //   return top7.sort(
  //     (a, b) => new Date(a.depDate).getTime() - new Date(b.depDate).getTime()
  //   );
  // }, [value]);

  // 출발일 버튼 노출 한도를 정의 (9개 초과 시 overflow)
  const MAX_DIRECT_BUTTON_COUNT = 8;
  const OVERFLOW_THRESHOLD = 9;
  const MORE_BUTTON_KEY = '__more__';

  const formatRouteText = (raw) => {
    if (Array.isArray(raw)) {
      return raw.filter(Boolean).join(', ');
    }
    if (typeof raw === 'string') {
      return raw;
    }
    return '';
  };

  // 2) UI 상태
  const [displayedDepDate, setDisplayedDepDate] = useState('');
  // 중복 날짜 문제 방지: 날짜 문자열 대신 고유 키로 선택 추적
  const [selectedKey, setSelectedKey] = useState('');
  const [selectedOverflowKey, setSelectedOverflowKey] = useState('');
  const chooseDateButtonRef = useRef(null); // overflow 버튼이 모달 트리거를 재사용하도록 ref 보관
  const cardDomId = cardId || `search-card-${index}`; // 정렬 변경 후에도 동일한 id 유지

  const toDay = (s = '') => (s || '').split('T')[0];
  const getItemKey = (item) => item?.idx || item?.codetocruiseid || toDay(item?.depDate);
  const computeLowestPrice = (item) => {
    if (!item) return Infinity;
    const prices = [item.cruiseinside, item.cruiseoutside, item.cruisebalcony, item.cruisesuite]
      .map((p) => (typeof p === 'string' ? Number(p.replace(/,/g, '')) : Number(p)))
      .filter((p) => Number.isFinite(p) && p > 0);
    // 화면에 표시하는 객실 요금의 최솟값으로 비교한다. 무요금 객실은 제외한다.
    return prices.length > 0 ? Math.min(...prices) : Infinity;
  };

  // 9개 초과 일정이 있는지 계산 → overflow 버튼 렌더 여부
  const hasOverflowDates = useMemo(
    () => sortedByDepDate.length > OVERFLOW_THRESHOLD,
    [sortedByDepDate]
  );

  // 버튼에 직접 노출할 일정(최대 8개)
  const visibleDepartureItems = useMemo(
    () => (hasOverflowDates ? sortedByDepDate.slice(0, MAX_DIRECT_BUTTON_COUNT) : sortedByDepDate),
    [hasOverflowDates, sortedByDepDate]
  );

  // overflow로 빠진 일정 리스트(더보기 버튼 대상)
  const overflowDepartureItems = useMemo(
    () => (hasOverflowDates ? sortedByDepDate.slice(MAX_DIRECT_BUTTON_COUNT) : []),
    [hasOverflowDates, sortedByDepDate]
  );

  const overflowCount = overflowDepartureItems.length;

  // 일정이 overflow 그룹에 속하는지 판단
  const isOverflowItem = (item) => {
    if (!item || !hasOverflowDates) return false;
    const key = getItemKey(item);
    return overflowDepartureItems.some((overflow) => getItemKey(overflow) === key);
  };

  // 출발일을 선택하면서 overflow 상태(더보기 선택 여부)까지 갱신
  const selectDepartureItem = (item) => {
    if (!item) return;
    const key = getItemKey(item);
    const day = toDay(item.depDate);
    if (isOverflowItem(item)) {
      setSelectedKey(MORE_BUTTON_KEY);
      setSelectedOverflowKey(key);
    } else {
      setSelectedKey(key);
      setSelectedOverflowKey('');
    }
    setDisplayedDepDate(day);
  };

  // 더보기 상태에서 실제 선택된 일정 객체 추적
  const overflowSelectedItem = useMemo(
    () => {
      if (!selectedOverflowKey) return undefined;
      return sortedByDepDate.find((item) => getItemKey(item) === selectedOverflowKey);
    },
    [selectedOverflowKey, sortedByDepDate]
  );

  // 더보기 버튼 텍스트에 선택된 overflow 날짜를 함께 보여줌
  const moreButtonLabel = useMemo(() => {
    if (!hasOverflowDates) return '';
    const base = `+${overflowCount}건 더보기`;
    if (selectedKey === MORE_BUTTON_KEY) {
      const target = overflowSelectedItem || overflowDepartureItems[0];
      if (target) {
        return <>
          <span>{base}</span>
          <span className='selected-schedule-label' style={{color:'#0066FF'}}>{toDay(target.depDate)}</span>
        </>;
      }
    }
    return base;
  }, [hasOverflowDates, overflowCount, selectedKey, overflowSelectedItem, overflowDepartureItems]);

  // 첫 번째 일정 자동 선택 ---------> 가격 가장 낮은게 선택(동률이면 출발일 빠른 순). 전부 0가면 가장 빠른 출발일 선택
  // overflow 항목이 최저가일 때는 더보기 버튼과 실제 키를 함께 세팅
  useEffect(() => {
    if (!sortedByDepDate.length) {
      setSelectedKey('');
      setSelectedOverflowKey('');
      setDisplayedDepDate('');
      return;
    }

    const availableKeys = new Set(sortedByDepDate.map((it) => getItemKey(it)));
    const hasValidDirectSelection =
      selectedKey &&
      selectedKey !== MORE_BUTTON_KEY &&
      availableKeys.has(selectedKey);
    const hasValidOverflowSelection =
      selectedKey === MORE_BUTTON_KEY &&
      selectedOverflowKey &&
      availableKeys.has(selectedOverflowKey);
    if (hasValidDirectSelection || hasValidOverflowSelection) return;

    const selectDefaultItem = (item) => {
      if (!item) return;
      const key = getItemKey(item);
      const day = toDay(item.depDate);
      const isOverflowItem = hasOverflowDates && overflowDepartureItems.some((overflow) => getItemKey(overflow) === key);
      if (isOverflowItem) {
        setSelectedKey(MORE_BUTTON_KEY);
        setSelectedOverflowKey(key);
      } else {
        setSelectedKey(key);
        setSelectedOverflowKey('');
      }
      setDisplayedDepDate(day);
    };

    const itemsWithPrice = sortedByDepDate
      .map((item) => ({ ...item, lowestPrice: computeLowestPrice(item) }))
      .filter((item) => Number.isFinite(item.lowestPrice)); // 유효한 객실 요금이 없는 일정은 제외

    if (itemsWithPrice.length > 0) {
      const lowestPrice = Math.min(...itemsWithPrice.map((item) => item.lowestPrice));
      const cheapestItems = itemsWithPrice.filter((item) => item.lowestPrice === lowestPrice);
      cheapestItems.sort((a, b) => new Date(a.depDate).getTime() - new Date(b.depDate).getTime());
      selectDefaultItem(cheapestItems[0]);
      return;
    }

    // 전부 0가: 가장 빠른 출발일을 선택
    selectDefaultItem(sortedByDepDate[0]);
  }, [
    sortedByDepDate,
    selectedKey,
    selectedOverflowKey,
    hasOverflowDates,
    overflowDepartureItems,
  ]);

  // 인라인 가격 → 옵션 배열 변환 (0은 숨김)
  const buildOptionPricesFromItem = (item) => {
    if (!item) return [];
    const leadRaw = item.leadprice;
    const leadNumeric = typeof leadRaw === 'number'
      ? leadRaw
      : Number(String(leadRaw ?? '').replace(/,/g, ''));
    const hasLeadPrice = Number.isFinite(leadNumeric) && leadNumeric > 0;
    const rows = [
      { name: '인사이드', key: 'cruiseinside' },
      { name: '오션뷰',   key: 'cruiseoutside' },
      { name: '발코니',   key: 'cruisebalcony' },
      { name: '스위트',   key: 'cruisesuite' },
    ];
    return rows
      .map(({ name, key }) => {
        const raw = item[key];
        const num = typeof raw === 'string' ? Number(raw.replace(/,/g, '')) : Number(raw ?? 0);
        if (!Number.isFinite(num) || num <= 0) {
          return hasLeadPrice ? { name, price: null, placeholder: true } : { name, price: 0, placeholder: false };
        }
        return { name, price: num, placeholder: false };
      })
      .filter(r => (r.placeholder ? true : r.price > 0)); // leadprice가 있으면 placeholder 유지
  };

  // 현재 선택된 출발건(없으면 첫 건)
  // 현재 선택된 일정(더보기 선택 시 overflow 대상에서 찾아옴)
  const currentItem = useMemo(() => {
    if (!sortedByDepDate.length) return undefined;
    if (selectedKey === MORE_BUTTON_KEY) {
      if (selectedOverflowKey) {
        const match = sortedByDepDate.find((v) => getItemKey(v) === selectedOverflowKey);
        if (match) return match;
      }
      return overflowDepartureItems[0] ?? sortedByDepDate[0];
    }
    return (
      sortedByDepDate.find((v) => getItemKey(v) === selectedKey) ||
      sortedByDepDate[0]
    );
  }, [sortedByDepDate, selectedKey, selectedOverflowKey, overflowDepartureItems]);

  // 선택된 일정에 따라 운항 경로를 표시 (없으면 기본 route 사용)
  const currentRouteText = useMemo(() => {
    if (currentItem?.route) {
      return formatRouteText(currentItem.route);
    }
    return formatRouteText(route);
  }, [currentItem, route]);

  // 현재 선택건의 옵션가
  const optionPrices = useMemo(
    () => buildOptionPricesFromItem(currentItem),
    [currentItem]
  );

  const renderOptionPrice = (opt) => {
    if (opt.placeholder) return '$';
    return Number((opt.price / 2)).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  };

  // 이미지 확대 확대
  const zoomInImg = (img) => {
    if (!img || img === NO_IMAGE_FALLBACK) return;
    alert({
      title: '이미지',
      content: <img src={img} alt='이미지 크게보기'/>,
      confirmButtonText: '닫기',
      // showConfirmButton: false,
    });
  };

  /* 이미지 hover시 floating message 관련 함수 */ 
  // hover
  const handleMouseEnter = () => {
    if (isTouchDevice) return;
    if (!displayImage || displayImage === NO_IMAGE_FALLBACK) return;
    setFloatingMsg('클릭하면 이미지를 크게 볼 수 있습니다.');
    setShowFloating(true);
  };

  // 움직일떄
  const handleMouseMove = (e) => {
    if (isTouchDevice) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setFloatingPos({
      x: e.clientX - rect.left + 10,
      y: e.clientY - rect.top + 10,
    });
  };

  // 마우스 leave
  const handleMouseLeave = () => {
    if (isTouchDevice) return;
    setShowFloating(false);
  };

  return (
    <div id={cardDomId} className='search-item-card'>
      <input type="hidden" value={`${index}`} />
      <h2 className='TA-item-card-title'>
        <span className='item-card-period'>{period}</span>
        <span>{name}</span>
        <span className='item-card-tonnage'>({tonnage ? `${tonnage}톤` : ''})</span>
      </h2>

      {/* 기존 + alert */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <img
          src={displayImage || NO_IMAGE_FALLBACK}
          alt='이미지'
          className='item-card-img'
          onClick={() => zoomInImg(displayImage || NO_IMAGE_FALLBACK)}
          onError={handleImageError}
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
        {showFloating && !isTouchDevice && (
          <div
            className="floating-message"
            style={{
              position: 'absolute',
              top: floatingPos.y,
              left: floatingPos.x,
              pointerEvents: 'none',
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: '#FFF',
              padding: '4px 8px',
              borderRadius: '6px',
              whiteSpace: 'nowrap',
              zIndex: 1000,
            }}
          >
            {floatingMsg}
          </div>
        )}
      </div>

      {/* 변경안_2 */}
      <div className='item-card-body'>
        <h2 className='item-card-title'>
          <span>{period}</span>
          <span>{name}</span>
          <span className='item-card-tonnage'>({tonnage ? `${tonnage}톤` : ''})</span>
        </h2>

        <div style={{ display:'flex', gap:'6px', flexDirection:'column' }}>
          <div className='item-card-content-wrapper'>
            {/* 운항 경로 */}
            <div className='item-card-content item-route'>
              <p className='title'>운항 경로</p>
              <span className='content'>{currentRouteText}</span>
            </div>

            {/* 가까운 출발일(버튼) */}
            <div className='item-card-content item-dep-date'>
              <p className='title'>가까운 출발일</p>
              {sortedByDepDate.length > 0 ? (
                <div className='content'>
                  {visibleDepartureItems.map((item) => {
                    const day = toDay(item.depDate);
                    const key = getItemKey(item);
                    const isSelected =
                      selectedKey === key ||
                      (selectedKey === MORE_BUTTON_KEY && selectedOverflowKey === key);
                    return (
                      <button
                        type='button'
                        className={`btn ${isSelected ? 'btn-selected' : 'btn-not-selected'}`}
                        key={key}
                        onClick={() => selectDepartureItem(item)}
                      >
                        {day}
                      </button>
                    );
                  })}
                  {hasOverflowDates && (
                    <button
                      type='button'
                      className={`more-schedule-btn btn ${selectedKey === MORE_BUTTON_KEY ? 'btn-selected' : 'btn-not-selected'}`}
                      key={MORE_BUTTON_KEY}
                      onClick={() => {
                        if (selectedKey === MORE_BUTTON_KEY && !selectedOverflowKey) {
                          const target = overflowDepartureItems[0];
                          if (target) {
                            selectDepartureItem(target);
                          }
                        }
                        if (chooseDateButtonRef.current) {
                          chooseDateButtonRef.current.click();
                        }
                      }}
                    >
                      {moreButtonLabel}
                    </button>
                  )}
                </div>
              ) : (
                <p>출항 정보가 없습니다.</p>
              )}
            </div>

            {/* 가격 블록 (인라인 값 사용) */}
            <div className='item-card-content item-dep-cabin'>
              <p className='title'>
                2인 1실 기준, 1인 최저 요금 <span>({displayedDepDate || '-'})</span>
              </p>

              <div className='content'>
                <div className='cabin-option-wrapper'>
                  {Array.isArray(optionPrices) && optionPrices.length > 0 ? (
                    optionPrices.map((opt, i) => (
                      <div key={i} className='cabin-option'>
                        <span>{opt.name}</span>
                        <span>{renderOptionPrice(opt)}</span>
                      </div>
                    ))
                  ) : (
                    <p className='no-price'>현재 해당 일정의 가격 정보가 제공되지 않습니다.</p>
                  )}
                </div>
              </div>
            </div>

            {/* 모달(리스트 전개) - 인라인 값만 표시 */}
            <CommonModal
              trigger={<button type='button' className='btn choose-dep-date-btn' ref={chooseDateButtonRef}>출발일 선택하기</button>}
              title="출발일 선택하기"
              showFooter={true}
              customFooterContent={(
                <div className='modal-select-date-footer'>
                  <div className="modal-selected-option-date-info">
                    <span>출발일</span><span>{displayedDepDate || '-'}</span>
                  </div>
                  <button className="btn modal-selected-option-date-btn" onClick={() => {
                    const effectiveKey = selectedKey === MORE_BUTTON_KEY ? selectedOverflowKey : selectedKey;
                    const selectedItem = value.find((item) => (item?.idx || item?.codetocruiseid || (item.depDate || '').split('T')[0]) === effectiveKey);
                    if (selectedItem) {
                      // 스크롤될 index 설정
                      document.cookie = `scrollBackId=${cardDomId}; path=/;`;
                      // console.log('value-idx',selectedItem.idx);
                      navigate(`/detail/${selectedItem.idx}`);
                    }
                  }}>
                    예약하기
                  </button>
                </div>
              )}
            >
              <div style={{ padding:'0 24px' }} className='modal-select-dep-date'>
                {sortedByDepDate.length > 0 ? (
                  sortedByDepDate.map((item) => {
                    const day = toDay(item.depDate);
                    const list = buildOptionPricesFromItem(item);
                    const key = getItemKey(item);
                    const selected =
                      selectedKey === key ||
                      (selectedKey === MORE_BUTTON_KEY && selectedOverflowKey === key);
                    return (
                      <div
                        key={key}
                        className={`modal-dep-date-option-item ${selected ? 'selected' : ''}`}
                        onClick={() => selectDepartureItem(item)}
                      >
                        {selected ? <RadioChk /> : <RadioNormal />}
                        <span className='option-date'>{day}</span>

                        <div className='modal-dep-date-option-item-value'>
                          {list.length > 0 ? (
                            list.map((opt, idx) => (
                              <div key={idx} className='value'>
                                <span className='value-name'>{opt.name}</span>
                                <span className='value-price'>{renderOptionPrice(opt)}</span>
                              </div>
                            ))
                          ) : (
                            <p className='no-price'>현재 해당 일정의 가격 정보가 제공되지 않습니다.</p>
                          )}
                        </div>

                        {/* <ul className='modal-dep-date-option-item-caution'>
                          <li>
                            <div className='dot-icon'><Dot /></div>
                            <p>Up to 30% OFF Cruises + Kids Sail FREE : on select sailings</p>
                          </li>
                          <li>
                            <div className='dot-icon'><Dot /></div>
                            <p>Military & Civil Service Rates - Must call to book</p>
                          </li>
                        </ul> */}
                      </div>
                    );
                  })
                ) : (
                  <p>선택 가능한 출발일이 없습니다.</p>
                )}
              </div>
            </CommonModal>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchCard;
